import React, { FunctionComponent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateShort, formatTime } from 'services/Utils';

import { IDevice } from 'interfaces';

interface IDevicesListItemProps {
    device: IDevice;
}

export const DevicesListItem: FunctionComponent<IDevicesListItemProps> = ({ device }) => {
    const { t, i18n } = useTranslation();

    const testsURL = `/devices/${device.id}/tests`;
    const editDeviceURL = `/devices/${device.id}`;

    let testsSpan: ReactNode;
    const hasTests = device.last_run_id;
    if (hasTests) {
        const isPassed = device.tests_passed === device.tests_total && device.tests_total > 0;

        testsSpan = (
            <>
                {isPassed
                    ? <FontAwesomeIcon icon="check-circle" className="devicesItem_testsPassedIcon" />
                    : <FontAwesomeIcon icon="exclamation-triangle" className="devicesItem_testsWarningIcon" />
                }
                {device.tests_passed}/{device.tests_total}
            </>
        );
    }

    // Each string goes in a div as well as a title, so precompute translations to avoid doing them twice.
    const t_firmware = t('devicesList.item.firmware');
    const t_serial = t('devicesList.item.serial');
    const t_location = t('devicesList.item.location');
    const t_firmwareTitle = t_firmware + ' ' + device.firmware.firmware_version;
    const t_serialTitle = t_serial + ' ' + device.serial_number;
    const t_locationTitle = t_location + ' ' + device.location;
    const t_lastRunDate = formatDateShort(device.last_run_at);
    const t_lastRunTime = formatTime(device.last_run_at);
    const t_addedDate = formatDateShort(device.created_at);
    const t_addedTime = formatTime(device.created_at);

    return (
        <li className="devicesItem" data-cy="deviceItem_device">
            <Link to={testsURL} className="devicesItem_testsLink" data-cy="deviceItem_testsLink">
                <div className="devicesItem_iconCol devicesItem_col">
                    <div className="devicesItem_iconCircle">
                        <FontAwesomeIcon icon="hdd" className="devicesItem_icon" />
                    </div>
                </div>
                <div className="devicesItem_makeModelCol devicesItem_col">
                    <div className="devicesItem_name ellipsis" title={device.model.name}>
                        {device.model.name}
                    </div>
                    <div className="devicesItem_manufacturer ellipsis" title={device.manufacturer.name}>
                        {device.manufacturer.name}
                    </div>
                </div>
                <div className="devicesItem_detailsCol devicesItem_col">
                    <div className="devicesItem_detailsLeft">
                        <div className="ellipsis" title={t_firmwareTitle}>
                            {t_firmware}
                        </div>
                        <div className="ellipsis" title={t_serialTitle}>
                            {t_serial}
                        </div>
                        <div className="ellipsis" title={t_locationTitle}>
                            {t_location}
                        </div>
                    </div>
                    <div className="devicesItem_detailsRight">
                        <div className="ellipsis" title={t_firmwareTitle}>
                            {device.firmware.firmware_version}
                        </div>
                        <div className="ellipsis" title={t_serialTitle}>
                            {device.serial_number}
                        </div>
                        <div className="ellipsis" title={t_locationTitle}>
                            {device.location}
                        </div>
                    </div>
                </div>
                <div className="devicesItem_testsCol devicesItem_col">
                    {hasTests && testsSpan}
                </div>
                <div className="devicesItem_lastRunCol devicesItem_col ellipsis" title={t_lastRunDate + ' ' + t_lastRunTime}>
                    {t_lastRunDate} <br/>
                    {t_lastRunTime}
                </div>
                <div className="devicesItem_addedDateCol devicesItem_col ellipsis" title={t_addedDate + ' ' + t_addedTime}>
                    {t_addedDate} <br/>
                    {t_addedTime}
                </div>
            </Link>
            <Link
                to={editDeviceURL}
                className="devicesItem_editCol"
                title={t('devicesList.item.editTooltip')}
                data-cy="deviceItem_editLink"
            >
                <FontAwesomeIcon icon={['far', 'edit']} className="devicesItem_editIcon" />
            </Link>
        </li>
    );
}